import React, { useEffect, useRef, useState } from 'react';
import { IconButton } from 'jl-design-system/components/button/Button';
import { ChevronLeftOutlined } from 'jl-design-system/components/icons/ChevronLeftOutlined';
import { ChevronRightOutlined } from 'jl-design-system/components/icons/ChevronRightOutlined';
import styles from './PrenavCarousel.scss';
import classNames from 'classnames';
import type { CarouselItem } from '../../types';

/* css width of the parent must be set manually when changing text length here */
export const carouselItems: CarouselItem[] = [
  {
    id: 'download-our-app',
    text: '',
    ctaText: 'Download the App for 10% off',
    href: '/customer-services/shopping-with-us/our-apps',
  },
  {
    id: 'our-price-promise',
    text: '',
    ctaText: 'Our Price Promise',
    href: '/customer-services/prices-and-payment/price-promise',
  },
];

export const PrenavCarousel = ({ className }: { className?: string }) => {
  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const [autoAdvanceStarted, setAutoAdvanceStarted] = useState<boolean>(false);
  const [hasFocus, setHasFocus] = useState(false);
  const autoAdvanceTimerRef = useRef<number | null>(null);

  const AUTO_ADVANCE_TIMEOUT = 5000;

  const handleFocus = () => {
    setHasFocus(true);
  };

  const handleBlur = () => {
    setHasFocus(false);
  };

  useEffect(
    function scheduleCarouselAutoAdvance() {
      if (autoAdvanceTimerRef.current) {
        window.clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }

      if (!autoAdvanceStarted) {
        setAutoAdvanceStarted(true);
      }

      if (hasFocus) {
        return;
      }

      autoAdvanceTimerRef.current = window.setTimeout(() => {
        setCarouselIndex(prev =>
          prev === carouselItems.length - 1 ? 0 : prev + 1
        );
      }, AUTO_ADVANCE_TIMEOUT);

      return () => {
        if (autoAdvanceTimerRef.current) {
          window.clearTimeout(autoAdvanceTimerRef.current);
          autoAdvanceTimerRef.current = null;
        }
      };
    },
    [carouselIndex, autoAdvanceStarted, hasFocus]
  );

  const changeCarouselIndex = (direction: number) => {
    if (autoAdvanceTimerRef.current) {
      window.clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }

    setCarouselIndex(prev => {
      const nextIndex =
        (prev + direction + carouselItems.length) % carouselItems.length;

      return nextIndex;
    });
  };

  const showPrevious = () => changeCarouselIndex(-1);
  const showNext = () => changeCarouselIndex(1);

  useEffect(function clearCarouselAutoAdvanceOnUnmount() {
    return () => {
      if (autoAdvanceTimerRef.current) {
        window.clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      className={classNames(styles.container, className)}
      data-testid="prenav-messages"
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <IconButton
        icon={ChevronLeftOutlined}
        size="small"
        background="clear"
        className={styles.chevronIcon}
        aria-label="Previous message"
        tabIndex={-1}
        onClick={showPrevious}
      />

      <ul
        className={classNames(styles.messages, {
          [styles.messages__ready]: autoAdvanceStarted,
          [styles.messages__focused]: hasFocus,
        })}
      >
        {carouselItems.map((item, index) => {
          const isActive = index === carouselIndex;
          const { id, href, text, ctaText } = item;

          return (
            <li
              key={id}
              className={classNames(styles.message, {
                [styles.message__visible]: isActive,
              })}
            >
              <a
                href={href}
                aria-label={`${text} ${ctaText}`}
                className={styles.link}
                onFocus={() => setCarouselIndex(index)}
              >
                {ctaText}
              </a>
            </li>
          );
        })}
      </ul>

      <IconButton
        icon={ChevronRightOutlined}
        size="small"
        background="clear"
        className={styles.chevronIcon}
        aria-label="Next message"
        tabIndex={-1}
        onClick={showNext}
      />
    </div>
  );
};
